import { Box, Typography } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { formatDefectDate, formatDefectDateTime } from "./defectUtils";

const getSortedLogs = (items = []) => {
  const safeItems = Array.isArray(items) ? items.filter(Boolean) : [];

  return [...safeItems].sort((a, b) => {
    const timeA = a?.dateTime ? new Date(a.dateTime).getTime() : 0;
    const timeB = b?.dateTime ? new Date(b.dateTime).getTime() : 0;
    return timeB - timeA;
  });
};

const splitDateTime = (dateTime) => {
  if (!dateTime) {
    return { date: "—", time: "" };
  }
  const full = formatDefectDateTime(dateTime);
  const parts = String(full).split(" · ");
  if (parts.length >= 2) {
    return { date: parts[0], time: parts.slice(1).join(" · ") };
  }
  return {
    date: formatDefectDate(dateTime) || full || "—",
    time: "",
  };
};

const DefectLogs = ({
  items = [],
  variant = "full",
  limit,
  onItemClick,
  emptyMessage = "No defect logs recorded.",
}) => {
  const sortedItems = getSortedLogs(items);
  const isCompact = variant === "compact";
  const visibleItems =
    typeof limit === "number" ? sortedItems.slice(0, limit) : sortedItems;

  if (!visibleItems.length) {
    return (
      <Typography className="text-sm text-slate-500">
        {emptyMessage}
      </Typography>
    );
  }

  return (
    <div className="flex flex-col">
      {visibleItems.map((item, index) => {
        const isLast = index === visibleItems.length - 1;
        const hasValueChange = Boolean(item?.fromValue || item?.toValue);
        const isClickable = Boolean(
          onItemClick && item?.entityType && item?.entityId
        );
        const entityLabel =
          item?.entityLabel ||
          (item?.entityType === "work_order"
            ? item?.entityId
            : item?.entityType === "defect"
              ? item?.entityId
              : "");
        const { date, time } = splitDateTime(item?.dateTime);

        return (
          <div
            key={`${item?.action || "Log"}-${item?.dateTime || index}-${index}`}
            className={`grid grid-cols-[80px_20px_1fr] gap-x-3 sm:grid-cols-[104px_24px_1fr] sm:gap-x-4 ${
              isLast ? "" : isCompact ? "pb-4" : "pb-5"
            }`}
          >
            {/* Left — date */}
            <div className="pt-0.5 text-right">
              <p className="text-[14px] font-semibold leading-5 text-[#18283D]">
                {date}
              </p>
              {time ? (
                <p className="mt-1 text-[14px] leading-none text-[#98A2B3]">
                  {time}
                </p>
              ) : null}
            </div>

            {/* Center — rail */}
            <div className="relative flex justify-center">
              {!isLast ? (
                <div className="absolute bottom-[-16px] left-1/2 top-[8px] w-px -translate-x-1/2 bg-[#D0D5DD] sm:bottom-[-20px]" />
              ) : null}
              <div
                className={`relative z-[1] mt-1 h-2.5 w-2.5 shrink-0 rounded-full border-2 sm:h-3 sm:w-3 ${
                  index === 0
                    ? "border-[#1D5BBF] bg-[#1D5BBF]"
                    : "border-[#1D5BBF] bg-white"
                }`}
              />
            </div>

            {/* Right — content without card border */}
            <Box
              className={`min-w-0 max-w-xl ${
                isClickable
                  ? "cursor-pointer rounded-md px-1 transition hover:bg-[#F8FAFC]"
                  : ""
              }`}
              onClick={() => {
                if (isClickable && onItemClick) {
                  onItemClick(item);
                }
              }}
              role={isClickable ? "button" : undefined}
              tabIndex={isClickable ? 0 : undefined}
              onKeyDown={(event) => {
                if (
                  isClickable &&
                  onItemClick &&
                  (event.key === "Enter" || event.key === " ")
                ) {
                  event.preventDefault();
                  onItemClick(item);
                }
              }}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p
                    className={`text-[14px] font-semibold leading-5 text-[#18283D] ${
                      isClickable ? "hover:text-[#1D5BBF]" : ""
                    }`}
                  >
                    {item?.action || "Activity Updated"}
                  </p>

                  <p className="mt-0.5 text-[14px] text-[#667085]">
                    {item?.user || "System"}
                  </p>

                  {hasValueChange ? (
                    <p className="mt-1.5 text-[14px] text-[#475467]">
                      <span className="text-[#98A2B3]">
                        {item?.fromValue || "—"}
                      </span>
                      <span className="mx-1.5 text-[#98A2B3]">→</span>
                      <span className="font-medium text-[#18283D]">
                        {item?.toValue || "—"}
                      </span>
                    </p>
                  ) : null}

                  {item?.details ? (
                    <p className="mt-1 text-[14px] leading-relaxed text-[#667085]">
                      {item.details}
                    </p>
                  ) : null}

                  {!isCompact && entityLabel ? (
                    <p className="mt-1 text-[14px] font-medium text-[#1D5BBF]">
                      Related: {entityLabel}
                    </p>
                  ) : null}
                </div>

                {isClickable ? (
                  <ChevronRightIcon
                    className="mt-0.5 shrink-0 text-[#98A2B3]"
                    fontSize="small"
                  />
                ) : null}
              </div>
            </Box>
          </div>
        );
      })}
    </div>
  );
};

export default DefectLogs;
